import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { PROJECTS } from "@/lib/data";
import { normalizeImage } from "@/lib/imageUtils";

/**
 * ProjectSlides — kingtide.com-inspired stacked deck.
 *
 * Architecture:
 *   • Section height = (total + 1) * 100vh. Sticky container (h-screen)
 *     stays pinned throughout the section's scroll.
 *   • Slide 0 (NORD) is the base — always visible at y:0. Later slides
 *     translateY from 100% → 0% during their equal scroll segment,
 *     covering the previous slide via z-index.
 *   • Uses a plain scroll listener (no framer useScroll) because Lenis
 *     smooth-scroll interacts badly with framer's useTransform+clamp,
 *     resetting the transform to `none` after the first scroll event.
 *   • Rounded top corners on every slide give the kingtide-style card
 *     silhouette during transit.
 */

const ORDER = ["monolith", "nord", "shopdog", "tungsten", "companeros", "wolfram", "kenya-safari-experience"];
const EASE = [0.16, 1, 0.3, 1];

function useSectionProgress(sectionRef) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const compute = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = el.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return setProgress(0);
      const scrolled = -rect.top;
      setProgress(Math.max(0, Math.min(1, scrolled / scrollable)));
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [sectionRef]);
  return progress;
}

function Slide({ p, index, total, progress }) {
  const [hovered, setHovered] = useState(false);

  // Slide 0 always y:0. Each subsequent slide reveals continuously over its
  // full segment so the stack transitions seamlessly (kingtide.com style).
  // The first reveal (slide 1) begins at progress 0 so there is no initial
  // scroll-dead-zone before the stack starts moving.
  const revealSlides = Math.max(total - 1, 1);
  const step = 1 / revealSlides;
  let translatePct;
  if (index === 0) {
    translatePct = 0;
  } else {
    const enterStart = (index - 1) * step;
    const enterEnd = enterStart + step;
    const local = (progress - enterStart) / (enterEnd - enterStart);
    const clamped = Math.max(0, Math.min(1, local));
    // Ease-out cubic for a soft landing
    const eased = 1 - Math.pow(1 - clamped, 3);
    translatePct = (1 - eased) * 105; // 105 → 0, small extra so slides stay fully offscreen before their reveal starts
  }

  const img = normalizeImage(p.images[0], p.title);
  const meta = [p.location, p.tag || p.projectType].filter(Boolean).join("  ·  ");

  return (
    <div
      style={{
        transform: `translate3d(0, ${translatePct}%, 0)`,
        zIndex: index + 1,
        willChange: "transform",
      }}
      className="absolute inset-0 md:inset-5"
    >
      {p.confidential ? (
        <div
          className="relative block h-full w-full overflow-hidden bg-[#0a0a0b] rounded-t-[28px] md:rounded-t-[36px]"
          data-testid={`project-slide-${p.slug}`}
        >
          <div className="absolute inset-0">
            <picture>
              {p.heroMobile?.src && (
                <source media="(max-width: 767px)" srcSet={p.heroMobile.src} />
              )}
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
            </picture>
          </div>
          <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
          <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 pt-24 pb-16 md:pt-28 md:pb-24">
            <div>
              <h2
                className="font-display uppercase text-white leading-[0.88] tracking-[-0.03em] whitespace-nowrap text-[10vw] md:text-[6vw] lg:text-[5vw]"
                data-testid={`project-slide-title-${p.slug}`}
              >
                {p.title.length > 14 ? (
                  <>
                    {p.title.split(" ").slice(0, -1).join(" ")}
                    <br />
                    {p.title.split(" ").slice(-1)}
                  </>
                ) : (
                  p.title
                )}
              </h2>
              <p className="mono-label text-white/85 mt-6 md:mt-10">{meta}</p>
              <p className="mt-3 md:mt-4 font-sans-hd text-white/90 text-sm md:text-lg leading-relaxed max-w-xl [text-wrap:balance]">
                {p.story}
              </p>
            </div>
          </div>
        </div>
      ) : (
      <Link
        to={`/work/${p.slug}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative block h-full w-full overflow-hidden group bg-[#0a0a0b] rounded-t-[28px] md:rounded-t-[36px]"
        data-testid={`project-slide-${p.slug}`}
        data-cursor="ENTER"
        aria-label={`Enter ${p.title} case study`}
      >
        {/* Cover image, held still.
            Mobile viewports (<768px) get the portrait heroMobile shot;
            desktop keeps the landscape hero via the fallback <img>. */}
        <div className="absolute inset-0">
          <picture>
            {p.heroMobile?.src && (
              <source media="(max-width: 767px)" srcSet={p.heroMobile.src} />
            )}
            <img
              src={img.src}
              alt={img.alt}
              className="w-full h-full object-cover"
            />
          </picture>
        </div>

        {/* Scrims */}
        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />

        {/* Content overlay */}
        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 pt-24 pb-16 md:pt-28 md:pb-24">
          <div>
            <h2
              className={`font-display uppercase text-white leading-[0.88] tracking-[-0.03em] whitespace-nowrap ${
                p.title.length > 14
                  ? "text-[6vw] md:text-[3.4vw] lg:text-[2.8vw]"
                  : "text-[10vw] md:text-[6vw] lg:text-[5vw]"
              }`}
              data-testid={`project-slide-title-${p.slug}`}
            >
              {p.title}
            </h2>

            <div className="mt-6 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
              <div className="flex-1 max-w-2xl">
                <p className="mono-label text-white/85">{meta}</p>
                <p className="mt-3 md:mt-4 font-sans-hd text-white/90 text-sm md:text-lg leading-relaxed max-w-xl [text-wrap:balance]">
                  {p.story}
                </p>
              </div>

              <motion.div
                animate={{ x: hovered ? 8 : 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="flex items-center gap-3 shrink-0 self-start md:self-end"
              >
                <span className="mono-label text-white pb-1 border-b border-white/80 group-hover:border-white transition-colors">
                  Enter Project
                </span>
                <motion.span
                  animate={{ x: hovered ? 4 : 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="mono-label text-white text-lg leading-none"
                  aria-hidden="true"
                >
                  ↗
                </motion.span>
              </motion.div>
            </div>
          </div>
        </div>
      </Link>
      )}
    </div>
  );
}

export default function ProjectSlides() {
  const sectionRef = useRef(null);
  const progress = useSectionProgress(sectionRef);

  const projects = ORDER
    .map((slug) => PROJECTS.find((p) => p.slug === slug))
    .filter(Boolean);
  const total = projects.length;

  return (
    <section
      ref={sectionRef}
      id="work"
      data-testid="project-slides"
      className="relative bg-[#0a0a0b]"
      style={{ height: `${(total + 1) * 100}vh` }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {projects.map((p, i) => (
          <Slide
            key={p.slug}
            p={p}
            index={i}
            total={total}
            progress={progress}
          />
        ))}
      </div>
    </section>
  );
}
